import React, { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { Link } from "react-router-dom";
import { api, getListItems } from "../../services/api";
import { getImageUrl } from "../../utils/imageUrl";
import { useAdaptiveImageTone } from "../../hooks/useAdaptiveImageTone";
import { useSiteContent, sectionField } from "../../hooks/useSiteContent";

const DEFAULT_BG = "/assets/img/bg/category_bg_1.png";

const CategoryOne = () => {
  const swiperRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [ready, setReady] = useState(false);
  const [subtitle, setSubtitle] = useState("Wonderful Place For You");
  const [title, setTitle] = useState("Tour Categories");
  const [bgImage, setBgImage] = useState(DEFAULT_BG);
  const palette = useAdaptiveImageTone(bgImage);
  const { content } = useSiteContent("home");

  useEffect(() => {
    let active = true;

    Promise.all([
      api.getCategories("tour").catch(() => null),
      api.getSettings().catch(() => null),
    ]).then(([catRes, settingsRes]) => {
      if (!active) return;

      const settings = settingsRes?.data || {};
      if (settings.tour_categories_subtitle) {
        setSubtitle(settings.tour_categories_subtitle);
      }
      if (settings.tour_categories_title) {
        setTitle(settings.tour_categories_title);
      }
      const bg = getImageUrl(settings.tour_categories_bg, "bg");
      if (bg) {
        setBgImage(bg);
      }

      const items = getListItems(catRes);
      if (items.length) {
        setCategories(
          items.map((item) => ({
            id: item.id,
            title: item.name,
            imgSrc:
              getImageUrl(item.image, "category") ||
              "/assets/img/category/category_1_1.jpg",
          }))
        );
      }
    }).finally(() => {
      if (active) setReady(true);
    });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!swiperRef.current) return;

    const swiperInstance = swiperRef.current.swiper;
    if (!swiperInstance) return;

    if (swiperInstance.autoplay) {
      swiperInstance.autoplay.start();
    }
    if (swiperInstance.pagination) {
      swiperInstance.pagination.renderBullet = function (index, className) {
        let formattedNumber = index + 1 < 10 ? "0" + (index + 1) : index + 1;
        return `<span class="${className} number">${formattedNumber}</span>`;
      };
    }
    swiperInstance.pagination.init();
    swiperInstance.pagination.update();

    const multiplier = {
      translate: 0.1,
      rotate: 0.01,
    };

    const calculateWheel = () => {
      const slides = document.querySelectorAll(".categorySlider .single");
      const allowTilt = window.innerWidth >= 992;
      slides.forEach((slide) => {
        if (!allowTilt) {
          slide.style.transform = "none";
          slide.style.transformOrigin = "center center";
          return;
        }
        const rect = slide.getBoundingClientRect();
        const r = window.innerWidth * 0.5 - (rect.x + rect.width * 0.5);
        let ty =
          Math.abs(r) * multiplier.translate - rect.width * multiplier.translate;
        if (ty < 0) ty = 0;
        const transformOrigin = r < 0 ? "left top" : "right top";
        slide.style.transform = `translate(0, ${ty}px) rotate(${
          -r * multiplier.rotate
        }deg)`;
        slide.style.transformOrigin = transformOrigin;
      });
    };

    const raf = () => {
      requestAnimationFrame(raf);
      calculateWheel();
    };
    const id = requestAnimationFrame(raf);
    return () => cancelAnimationFrame(id);
  }, [categories]);

  if (!ready || !categories.length) {
    return null;
  }

  return (
    <section
      className={`category-area bg-top-center twa-banner-tone twa-banner-tone--${palette.tone} twa-category-tone`}
      style={{
        backgroundImage: `url(${bgImage})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        "--twa-banner-text": palette.textColor,
        "--twa-banner-muted": palette.mutedColor,
        "--twa-banner-accent": palette.accentColor,
        "--twa-banner-shadow": palette.shadow,
        "--twa-section-overlay": palette.sectionOverlay || palette.overlay,
        "--twa-btn-bg": palette.btnPrimaryBg,
        "--twa-btn-color": palette.btnPrimaryText,
        "--twa-btn-border": palette.btnPrimaryBorder,
        "--twa-btn-hover-bg": palette.btnPrimaryHoverBg,
        "--twa-btn-hover-color": palette.btnPrimaryHoverText,
        "--twa-btn2-bg": palette.btnSecondaryBg,
        "--twa-btn2-color": palette.btnSecondaryText,
        "--twa-btn2-border": palette.btnSecondaryBorder,
        "--twa-btn2-hover-bg": palette.btnSecondaryHoverBg,
        "--twa-btn2-hover-color": palette.btnSecondaryHoverText,
      }}
    >
      <div
        className="twa-banner-overlay"
        style={{ background: palette.sectionOverlay || palette.overlay }}
        aria-hidden="true"
      />
      <div className="container th-container">
        <div className="title-area text-center">
          <span className="sub-title">{subtitle}</span>
          <h2 className="sec-title">{title}</h2>
        </div>

        <Swiper
          key={categories.map((c) => c.id).join("-")}
          ref={swiperRef}
          modules={[Pagination, Autoplay]}
          slidesPerView={1}
          breakpoints={{
            0: { slidesPerView: 1, spaceBetween: 12 },
            576: { slidesPerView: 2, spaceBetween: 16 },
            768: { slidesPerView: 2, spaceBetween: 20 },
            992: { slidesPerView: 3, spaceBetween: 24 },
            1200: { slidesPerView: 3, spaceBetween: 28 },
            1400: { slidesPerView: 5, spaceBetween: 32 },
          }}
          spaceBetween={12}
          loop={categories.length > 2}
          autoplay={{ delay: 3000, disableOnInteraction: false }}
          speed={1000}
          pagination={{
            clickable: true,
            el: ".swiper-pagination",
            type: "bullets",
          }}
          className="th-slider has-shadow categorySlider"
        >
          {categories.map((category) => (
            <SwiperSlide key={category.id}>
              <div className="category-card single">
                <div className="box-img global-img">
                  <img
                    src={category.imgSrc}
                    alt={`${category.title} tour category - Travel With Ammar`}
                    loading="lazy"
                  />
                </div>
                <h3 className="box-title">
                  <Link to="/tour">{category.title}</Link>
                </h3>
                <Link className="line-btn" to="/tour">
                  {sectionField(content, "categories", "see_more_text", "See More")}
                </Link>
              </div>
            </SwiperSlide>
          ))}
          <div className="slider-controller w-100 justify-content-center">
            <div className="swiper-pagination" style={{ maxWidth: "100%" }} />
          </div>
        </Swiper>
      </div>
    </section>
  );
};

export default CategoryOne;
